import React from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Award, Loader2, Gift, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function LoyaltyOverview({ email }) {
  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ['loyalty-bookings', email],
    queryFn: () => base44.entities.Booking.filter({ customer_email: email }, '-date', 500),
  });

  if (isLoading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  const qualifying = bookings.filter((b) => (b.status === 'confirmed' || b.status === 'completed') && !b.is_loyalty_free).length;
  const earned = Math.floor(qualifying / 20);
  const redeemed = bookings.filter((b) => b.is_loyalty_free).length;
  const balance = Math.max(0, earned - redeemed);
  const progress = qualifying % 20;
  const toNext = 20 - progress;

  return (
    <div className="space-y-6">
      <div className="bg-[#111115] border border-white/5 p-6">
        <div className="flex items-center gap-2 mb-1">
          <Award className="w-4 h-4 text-primary" />
          <p className="text-primary text-[10px] font-bold tracking-widest">LOYALTY SCHEME</p>
        </div>
        <h2 className="text-white font-black text-xl uppercase">1 Free Session For Every 20</h2>
        <p className="text-white/40 text-sm mt-1">Every 20 qualifying sessions you book earns 1 free session, redeemable on any future booking.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-[#111115] border border-white/5 p-4">
          <p className="text-white/30 text-[10px] font-bold tracking-widest">SESSIONS</p>
          <p className="text-white font-black text-xl mt-1">{qualifying}</p>
        </div>
        <div className="bg-[#111115] border border-white/5 p-4">
          <p className="text-white/30 text-[10px] font-bold tracking-widest">EARNED</p>
          <p className="text-white font-black text-xl mt-1">{earned}</p>
        </div>
        <div className="bg-[#111115] border border-white/5 p-4">
          <p className="text-white/30 text-[10px] font-bold tracking-widest">REDEEMED</p>
          <p className="text-white font-black text-xl mt-1">{redeemed}</p>
        </div>
        <div className="bg-primary/5 border border-primary/20 p-4">
          <p className="text-primary text-[10px] font-bold tracking-widest">AVAILABLE</p>
          <p className="text-primary font-black text-xl mt-1">{balance}</p>
        </div>
      </div>

      <div className="bg-[#111115] border border-white/5 p-6">
        <div className="flex items-center justify-between mb-3">
          <p className="text-white/40 text-xs font-bold tracking-widest">PROGRESS TO NEXT FREE SESSION</p>
          <p className="text-white text-sm font-bold">{progress}/20</p>
        </div>
        <div className="w-full h-2 bg-white/5 overflow-hidden">
          <div className="h-full bg-primary" style={{ width: `${(progress / 20) * 100}%` }} />
        </div>
        <p className="text-white/30 text-xs mt-3">
          {balance > 0
            ? `You have ${balance} free session(s) ready to redeem now.`
            : `${toNext} more qualifying session${toNext !== 1 ? 's' : ''} to your next free session.`}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        {balance > 0 ? (
          <Link to="/bookings" className="bg-primary/20 border border-primary/40 hover:bg-primary hover:border-primary text-white font-bold text-xs tracking-widest px-6 py-3.5 transition-all flex items-center gap-2 justify-center">
            <Gift className="w-4 h-4" /> REDEEM FREE SESSION
          </Link>
        ) : (
          <Link to="/bookings" className="bg-primary/20 border border-primary/40 hover:bg-primary hover:border-primary text-white font-bold text-xs tracking-widest px-6 py-3.5 transition-all flex items-center gap-2 justify-center">
            <TrendingUp className="w-4 h-4" /> BOOK A SESSION
          </Link>
        )}
        <Link to="/account" className="border border-white/15 text-white/60 hover:text-white font-bold text-xs tracking-widest px-6 py-3.5 transition-all flex items-center justify-center">
          VIEW MY BOOKINGS
        </Link>
      </div>
    </div>
  );
}